import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import environment from '@environments/environment';
import { convertQueryParamsForApi } from '@data-access/shared/utils';
import { ListReviewLocationSchema } from '../types/review-location.types';
import {
  AppealsWorkspaceCreate,
  BaseQueryParams,
  MTRWorkspaceCreate,
  SiteWorkspaceCreate,
  StudyWorkspaceCreate,
  SubmissionResponse,
  WorkspaceSubTypeV2ListResponse,
  WorkspaceSubTypeV2QP,
  WorkspaceTypeListResponse,
} from '../types';
import { Observable } from 'rxjs';
import {
  SitesWorkspaceQP,
  SiteWorkspaceResponseList,
} from '@data-access/studies';

const SUBMISSIONS_URL = `${environment.irisApi}/v2/workspace`;

@Injectable({
  providedIn: 'root',
})
export class SubmissionSetupService {
  private readonly _http = inject(HttpClient);

  /**
   * A method to get review locations.
   * @returns An Observable of ListReviewLocationSchema containing the review locations data.
   */
  getReviewLocations() {
    return this._http.get<ListReviewLocationSchema>(
      `${environment.irisApi}/v2/review_locations/`,
    );
  }

  /**
   * Get sites for workspace by region
   */
  getSitesForWorkspaceByRegion(params: {
    submissionId: string;
    queryParams: SitesWorkspaceQP;
  }): Observable<SiteWorkspaceResponseList> {
    const { submissionId, ...queryParams } = params;
    const apiParams = { ...queryParams };

    return this._http.get<SiteWorkspaceResponseList>(
      `${SUBMISSIONS_URL}/${submissionId}/region_site/`,
      {
        params: convertQueryParamsForApi(apiParams.queryParams),
      },
    );
  }

  /**
   * Get sites by workspace region when submission information is retrieved.
   * Convenience method that can be called after retrieving submission information.
   * @param submissionId - The submission/workspace ID from the retrieved submission
   * @param queryParams - Optional query parameters for filtering and pagination. Defaults to page=1, page_size=10 if not provided.
   * @returns An Observable of SiteWorkspaceResponseList containing the sites data
   */
  getSitesByWorkspaceRegion(
    submissionId: string,
    queryParams?: Partial<SitesWorkspaceQP>,
  ): Observable<SiteWorkspaceResponseList> {
    const defaultParams: SitesWorkspaceQP = {
      page: 1,
      page_size: 10,
      ...queryParams,
    };
    return this.getSitesForWorkspaceByRegion({
      submissionId,
      queryParams: defaultParams,
    });
  }

  /**
   * Create a new study workspace.
   * @param data StudyWorkspaceCreate payload
   * @returns Observable of SubmissionResponse
   */
  createStudyWorkspace(data: StudyWorkspaceCreate) {
    return this._http.post<SubmissionResponse>(
      `${SUBMISSIONS_URL}/new-study/`,
      data,
    );
  }

  /**
   * Create a new site workspace (CIR - Add PI/Site to Existing Study).
   * @param data SiteWorkspaceCreate payload
   * @returns Observable of SubmissionResponse
   */
  createSiteWorkspace(data: SiteWorkspaceCreate) {
    return this._http.post<SubmissionResponse>(
      `${SUBMISSIONS_URL}/new-site/`,
      data,
    );
  }

  /**
   * A method to get submission sub-types with optional query parameters.
   * @param params WorkspaceSubTypeV2QP
   * @returns An Observable of WorkspaceSubTypeV2ListResponse containing the submission sub-types.
   */
  getSubmissionSubTypes(params?: WorkspaceSubTypeV2QP) {
    return this._http.get<WorkspaceSubTypeV2ListResponse>(
      `${SUBMISSIONS_URL}/sub_type/`,
      { params: params ? convertQueryParamsForApi(params) : undefined },
    );
  }

  /**
   * A method to get submission types with optional query parameters.
   * @param params BaseQueryParams
   * @returns An Observable of WorkspaceTypeListResponse containing the submission types.
   */
  getSubmissionTypes(params?: BaseQueryParams) {
    return this._http.get<WorkspaceTypeListResponse>(
      `${SUBMISSIONS_URL}/type/`,
      { params: params ? convertQueryParamsForApi(params) : undefined },
    );
  }

  /**
   * Create a new MTR (Modification to Research) workspace.
   * @param data MTRWorkspaceCreate payload
   * @returns Observable of SubmissionResponse
   */
  createMTRWorkspace(data: MTRWorkspaceCreate) {
    return this._http.post<SubmissionResponse>(`${SUBMISSIONS_URL}/mtr/`, data);
  }

  /**
   * Create a new Appeal workspace.
   * @param submissionId ID of the submission to appeal
   * @returns Observable of SubmissionResponse
   */
  createAppealWorkspace(workspaceId: string, data: AppealsWorkspaceCreate) {
    return this._http.post<SubmissionResponse>(
      `${SUBMISSIONS_URL}/${workspaceId}/appeal/`,
      data,
    );
  }
}
